// FloatingPromptAssistant.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, 
  X, 
  Move, 
  ChevronUp, 
  ChevronDown,
  BookOpen,
  Play,
  Pause,
  Square
} from 'lucide-react';
import CustomPrompts from './../components/customPrompts/CustomPrompts';

const FloatingPromptAssistant = ({
  userId,
  onUsePrompt,
  onUpgrade,
  gameInfo = null,
  isMobile = false,
  messages = [],
  isVisible = false,
  onClose
}) => {
  // State management
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ 
    x: isMobile ? 20 : window.innerWidth - 320, 
    y: isMobile ? 120 : 100 
  });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [showHint, setShowHint] = useState(true);
  
  // Queue system for running multiple prompts
  const [promptQueue, setPromptQueue] = useState([]);
  const [isRunningQueue, setIsRunningQueue] = useState(false);
  const [currentQueueIndex, setCurrentQueueIndex] = useState(0);
  
  // better floating interaction
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Refs
  const containerRef = useRef(null);
  const dragRef = useRef(null);

  const handleModalStateChange = useCallback((isOpen) => {
    setIsModalOpen(isOpen);
  }, []);
  
  // Auto-hide hint after delay
  useEffect(() => {
    if (showHint) {
      const timer = setTimeout(() => setShowHint(false), 8000);
      return () => clearTimeout(timer);
    }
  }, [showHint]);
  
  // Dragging functionality - Allow dragging when expanded too
  const handleMouseDown = useCallback((e) => {
    if (!dragRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    setIsDragging(true);
  }, []);
  
  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;
    
    // Keep within viewport bounds
    const maxX = window.innerWidth - (isExpanded ? 400 : 200);
    const maxY = window.innerHeight - (isExpanded ? 500 : 80);
    
    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY))
    });
  }, [isDragging, dragOffset, isExpanded]);
  
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);
  
  // Touch events for mobile - Allow dragging when expanded
  const handleTouchStart = useCallback((e) => {
    if (!dragRef.current) return;
    
    const touch = e.touches[0];
    const rect = containerRef.current.getBoundingClientRect();
    setDragOffset({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    });
    setIsDragging(true);
  }, []);
  
  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault();
    
    const touch = e.touches[0];
    const newX = touch.clientX - dragOffset.x;
    const newY = touch.clientY - dragOffset.y;
    
    const maxX = window.innerWidth - (isExpanded ? 350 : 180);
    const maxY = window.innerHeight - (isExpanded ? 400 : 70);
    
    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY))
    });
  }, [isDragging, dragOffset, isExpanded]);
  
  // Global event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove]);
  
  // Handle prompt selection
  const handlePromptSelected = useCallback((promptText, promptName) => {
    if (onUsePrompt) {
      onUsePrompt(promptText);
    }
    
    // Auto-minimize after use (optional)
    if (!isRunningQueue) {
      setTimeout(() => setIsExpanded(false), 1000);
    }
  }, [onUsePrompt, isRunningQueue]);
  
  // Queue management
  const addToQueue = useCallback((prompt) => {
    setPromptQueue(prev => [...prev, prompt]);
  }, []);
  
  const removeFromQueue = useCallback((index) => {
    setPromptQueue(prev => prev.filter((_, i) => i !== index));
  }, []);
  
  const runQueue = useCallback(async () => {
    if (promptQueue.length === 0) return;
    
    setIsRunningQueue(true);
    setCurrentQueueIndex(0);
    
    for (let i = 0; i < promptQueue.length; i++) {
      setCurrentQueueIndex(i);
      const prompt = promptQueue[i];
      
      if (onUsePrompt) {
        onUsePrompt(prompt.promptText);
      }
      
      // Wait between prompts (adjust timing as needed)
      if (i < promptQueue.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }
    
    setIsRunningQueue(false);
    setCurrentQueueIndex(0);
    setPromptQueue([]);
  }, [promptQueue, onUsePrompt]);
  
  const stopQueue = useCallback(() => {
    setIsRunningQueue(false);
    setCurrentQueueIndex(0);
  }, []);

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        ref={containerRef}
        initial={{ opacity: 0, scale: 0.8, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.8, y: 20 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="fixed z-50 select-none"
        style={{
          left: position.x,
          top: position.y,
          width: isExpanded ? (isMobile ? '350px' : '420px') : (isMobile ? '180px' : '200px'),
          height: isExpanded ? (isMobile ? '450px' : '550px') : 'auto',
          zIndex: 50
        }}
      >
        {/* Hint bubble - appears first */}
        <AnimatePresence>
          {showHint && !isExpanded && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.9 }}
              transition={{ type: 'spring', damping: 20, stiffness: 400 }}
              className="absolute bottom-full mb-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-xs px-4 py-2 rounded-lg shadow-lg whitespace-nowrap border border-emerald-400/20"
            >
              Use your custom prompts?
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-emerald-500" />
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Main container - Enhanced professional styling */}
        <motion.div
          className={`backdrop-blur-sm border rounded-xl overflow-hidden transition-all duration-300 ${
            isExpanded 
              ? 'bg-gradient-to-b from-slate-800/95 to-slate-900/98 border-slate-600/50 shadow-2xl shadow-black/20' 
              : 'bg-slate-800/95 border-slate-700/50 shadow-2xl'
          } ${isDragging ? 'cursor-grabbing shadow-3xl scale-105' : ''}`}
          layout
        >
          {/* Header - Enhanced for professional look when expanded */}
          <div
            ref={dragRef}
            className={`flex items-center justify-between p-3 border-b transition-all duration-300 ${
              isExpanded 
                ? 'bg-gradient-to-r from-slate-800/90 to-slate-900/95 border-slate-600/30 shadow-sm' 
                : 'bg-gradient-to-r from-slate-800 to-slate-900 border-slate-700/50'
            } ${!isMobile ? 'cursor-grab active:cursor-grabbing' : ''}`}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          >
            <div className="flex items-center gap-2">
              <div className={`rounded-lg transition-all duration-300 ${
                isExpanded 
                  ? 'p-2 bg-gradient-to-br from-emerald-500/20 to-violet-500/20 shadow-inner' 
                  : 'p-1.5 bg-emerald-500/20'
              }`}>
                <Sparkles className={`text-emerald-400 transition-all duration-300 ${
                  isExpanded ? 'w-5 h-5' : (isMobile ? 'w-4 h-4' : 'w-4 h-4')
                }`} />
              </div>
              <div>
                <h3 className={`font-medium text-white transition-all duration-300 ${
                  isExpanded ? 'text-base' : (isMobile ? 'text-sm' : 'text-sm')
                }`}>
                  {isExpanded ? 'Prompt Assistant' : 'Prompts'}
                </h3>
                {isExpanded && (
                  <p className="text-slate-400 text-xs">Quick access to your custom strategies</p>
                )}
              </div>
              {!isMobile && (
                <Move className="w-3 h-3 text-slate-400" />
              )}
            </div>
            
            <div className="flex items-center gap-2">
              {/* Queue indicator - Enhanced */}
              {promptQueue.length > 0 && (
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-violet-400 rounded-full animate-pulse" />
                  <span className="bg-gradient-to-r from-violet-500/20 to-purple-500/20 border border-violet-500/30 text-violet-300 text-xs px-2.5 py-1 rounded-full font-medium">
                    {promptQueue.length}
                  </span>
                </div>
              )}
              
              {/* Expand/collapse button - Enhanced */}
              <button
                onClick={() => {
                  setIsExpanded(!isExpanded);
                  setShowHint(false);
                }}
                className={`p-2 text-slate-400 hover:text-white rounded-lg transition-all ${
                  isExpanded 
                    ? 'hover:bg-emerald-500/10 border border-emerald-500/20' 
                    : 'hover:bg-white/10'
                }`}
                title={isExpanded ? 'Minimize' : 'Expand'}
              >
                {isExpanded ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
              
              {/* Close button - Enhanced */}
              <button
                onClick={onClose}
                className={`p-2 text-slate-400 hover:text-white rounded-lg transition-all ${
                  isExpanded 
                    ? 'hover:bg-red-500/10 border border-red-500/20' 
                    : 'hover:bg-white/10'
                }`}
                title="Close assistant"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* Expanded content - Streamlined professional styling */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden bg-gradient-to-b from-slate-800/50 to-slate-900/80"
                style={{
                  // 🆕 ADDED: Better overflow handling when modal is open
                  overflow: isModalOpen ? 'visible' : 'hidden'
                }}
              >
                {/* Queue controls - Only show if there's a queue */}
                {promptQueue.length > 0 && (
                  <div className="mx-4 mt-4 mb-3 p-4 bg-gradient-to-br from-violet-500/10 to-purple-500/10 border border-violet-500/20 rounded-xl shadow-inner">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-violet-500/20 rounded-lg">
                          <Play className="w-3.5 h-3.5 text-violet-400" />
                        </div>
                        <div>
                          <span className="text-sm font-medium text-white">Automation Queue</span>
                          <span className="ml-2 bg-violet-500/30 text-violet-300 text-xs px-2 py-0.5 rounded-full">
                            {promptQueue.length} prompts
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {!isRunningQueue ? (
                          <button
                            onClick={runQueue}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white text-xs font-medium rounded-lg transition-all shadow-sm"
                            title="Execute queue"
                          >
                            <Play className="w-3 h-3" />
                            Run All
                          </button>
                        ) : (
                          <button
                            onClick={stopQueue}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white text-xs font-medium rounded-lg transition-all shadow-sm"
                            title="Stop execution"
                          >
                            <Square className="w-3 h-3" />
                            Stop
                          </button>
                        )}
                        <button
                          onClick={() => setPromptQueue([])}
                          className="p-1.5 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                          title="Clear queue"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="space-y-2 max-h-24 overflow-y-auto scrollbar-thin scrollbar-thumb-violet-500/30 scrollbar-track-transparent">
                      {promptQueue.map((prompt, index) => (
                        <div
                          key={index}
                          className={`relative px-3 py-2 rounded-lg border transition-all ${
                            isRunningQueue && index === currentQueueIndex
                              ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-300 shadow-sm shadow-emerald-500/20'
                              : 'bg-slate-800/50 border-slate-700/30 text-slate-300 hover:bg-slate-700/50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                isRunningQueue && index === currentQueueIndex
                                  ? 'bg-emerald-400 animate-pulse'
                                  : index < currentQueueIndex && isRunningQueue
                                  ? 'bg-gray-400'
                                  : 'bg-violet-400'
                              }`} />
                              <span className="text-sm font-medium truncate">{prompt.name}</span>
                            </div>
                            <button
                              onClick={() => removeFromQueue(index)}
                              className="ml-2 p-1 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors flex-shrink-0"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Custom prompts - Simple scrollable container */}
                <div className="px-4 pb-4">
                  <div 
                    className="bg-slate-800/30 border border-slate-700/30 rounded-xl shadow-inner overflow-hidden"
                    style={{
                      // 🆕 ADDED: Remove height constraint when modal is open
                      maxHeight: isModalOpen ? 'none' : '320px'
                    }}
                  >
                    <div 
                      className="p-4 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-600/50 scrollbar-track-transparent"
                      style={{
                        // 🆕 ADDED: Remove height constraint when modal is open
                        maxHeight: isModalOpen ? 'none' : '320px'
                      }}
            >
                      <CustomPrompts
                        userId={userId}
                        gameInfo={gameInfo}
                        onUsePrompt={handlePromptSelected}
                        onUpgrade={onUpgrade}
                        showInSuggestions={true}
                        isMobile={isMobile}
                        showQueueOption={true}
                        onAddToQueue={addToQueue}
                        onModalStateChange={handleModalStateChange}
                        usePortalsForModals={true}  // 🆕 ADD THIS LINE

                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Minimized preview */}
          {!isExpanded && (
            <div className="p-2">
              <div className={`text-center ${isMobile ? 'text-xs' : 'text-xs'} text-slate-400`}>
                Click to expand
              </div>
            </div>
          )}
        </motion.div>
        
        {/* Professional scrollbar styles */}
        <style jsx="true">{`
          .scrollbar-thin {
            scrollbar-width: thin;
            scrollbar-color: rgba(71, 85, 105, 0.5) transparent;
          }
          
          .scrollbar-thin::-webkit-scrollbar {
            width: 6px;
            height: 6px;
          }
          
          .scrollbar-thin::-webkit-scrollbar-track {
            background: transparent;
            border-radius: 3px;
          }
          
          .scrollbar-thin::-webkit-scrollbar-thumb {
            background: rgba(71, 85, 105, 0.5);
            border-radius: 3px;
            transition: background 0.2s ease;
          }
          
          .scrollbar-thin::-webkit-scrollbar-thumb:hover {
            background: rgba(71, 85, 105, 0.7);
          }
          
          .scrollbar-thumb-emerald {
            scrollbar-color: rgba(52, 211, 153, 0.5) transparent;
          }
          
          .scrollbar-thumb-emerald::-webkit-scrollbar-thumb {
            background: rgba(52, 211, 153, 0.5);
          }
          
          .scrollbar-thumb-emerald::-webkit-scrollbar-thumb:hover {
            background: rgba(52, 211, 153, 0.7);
          }
          
          .scrollbar-thumb-violet-500\\/30::-webkit-scrollbar-thumb {
            background: rgba(139, 92, 246, 0.3);
          }
          
          .scrollbar-thumb-violet-500\\/30::-webkit-scrollbar-thumb:hover {
            background: rgba(139, 92, 246, 0.5);
          }
          
          .scrollbar-thumb-slate-600\\/50::-webkit-scrollbar-thumb {
            background: rgba(71, 85, 105, 0.5);
          }
          
          .scrollbar-thumb-slate-600\\/50::-webkit-scrollbar-thumb:hover {
            background: rgba(71, 85, 105, 0.7);
          }
        `}</style>
      </motion.div>
    </AnimatePresence>
  );
};

export default FloatingPromptAssistant;